import React, { useState } from 'react';
import { QBTestQuestionEto, QbTestCoursesGroupEto } from '../../../lib/api/types';
import { useWhiteboardStore } from '../../../store/useWhiteboardStore';
import { getGlassClasses } from '../../../utils/glassStyles';
import { useTheme } from '../../../hooks/useDarkMode';
import TestCompleteConfirmModal from '../../TestCompleteConfirmModal';
import { OpticalAnswer } from '../../../types';

interface OpticalAnswerPanelProps {
  questions: QBTestQuestionEto[];
  currentQuestionIndex: number;
  opticalAnswers: Record<string, OpticalAnswer>;
  eliminatedChoices: Record<string, Set<string>>;
  onAnswerSelect: (choice: 'A' | 'B' | 'C' | 'D' | 'E' | '') => void;
  onChoiceEliminate: (choice: string) => void;
  onQuestionNavigate: (index: number) => void;
  onClose: () => void;
  qbGroups?: QbTestCoursesGroupEto[];
  onTestComplete?: () => void;
  showCompleteButton?: boolean;
}

export const OpticalAnswerPanel: React.FC<OpticalAnswerPanelProps> = ({
  questions,
  currentQuestionIndex,
  opticalAnswers,
  eliminatedChoices,
  onAnswerSelect,
  onChoiceEliminate,
  onQuestionNavigate,
  onClose,
  qbGroups,
  onTestComplete,
  showCompleteButton = true
}) => {
  const { glassEffects, devicePreferences } = useWhiteboardStore();
  const { isDark } = useTheme();
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number>(0);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isUserSelection, setIsUserSelection] = useState(false);
  
  // Update selected group when current question changes (only if not user selection)
  React.useEffect(() => {
    if (!isUserSelection && qbGroups && qbGroups.length > 0 && currentQuestionIndex >= 0 && questions[currentQuestionIndex]) {
      const currentQuestion = questions[currentQuestionIndex];
      
      // Find which group contains the current question
      let foundGroupIndex = -1;
      
      for (let groupIndex = 0; groupIndex < qbGroups.length; groupIndex++) {
        const group = qbGroups[groupIndex];
        const hasQuestion = group.courses?.some(course => 
          course.questions?.some(q => q.questionId === currentQuestion.questionId)
        );
        
        if (hasQuestion) {
          foundGroupIndex = groupIndex;
          break;
        }
      }
      
      if (foundGroupIndex >= 0 && foundGroupIndex !== selectedGroupIndex) {
        setSelectedGroupIndex(foundGroupIndex);
      }
    }
  }, [currentQuestionIndex, qbGroups, questions]); // Removed selectedGroupIndex from dependencies
  
  // Reset user selection flag after a short delay
  React.useEffect(() => {
    if (isUserSelection) {
      const timer = setTimeout(() => {
        setIsUserSelection(false);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isUserSelection]);
  
  const renderQuestionItem = (
    question: QBTestQuestionEto,
    globalIndex: number,
    displayNumber: number
  ) => {
    const isCurrentQuestion = globalIndex === currentQuestionIndex;
    const rawAnswer = opticalAnswers[question.questionId || ''];
    // Filter out '0' for display (means visited but unanswered)
    const answer = rawAnswer === '0' ? undefined : rawAnswer;
    const eliminated = eliminatedChoices[question.questionId || ''];
    
    return (
      <div
        key={question.questionId || globalIndex}
        className="mb-1.5"
      >
        <div
          onClick={() => onQuestionNavigate(globalIndex)}
          className={`
            relative flex items-center gap-2 p-2 px-3 rounded-lg cursor-pointer transition-all
            ${isCurrentQuestion 
              ? 'bg-blue-50 border-2 border-blue-400 shadow-sm' 
              : 'hover:bg-gray-50 border-2 border-transparent'
            }
          `}
        >
          {/* Answer feedback indicator - positioned on left border */}
          {answer && question.answer && (
            <div className="absolute -left-2 top-1/2 -translate-y-1/2 bg-white rounded-full p-0.5 shadow-sm">
              {answer === question.answer ? (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8l3 3 7-7" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M4 4l8 8M12 4l-8 8" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
          )}
          
          <span className={`
            text-base font-bold min-w-[28px]
            ${isCurrentQuestion ? 'text-blue-600' : 'text-gray-700'}
          `}>
            {displayNumber}.
          </span>
        
          <div className="flex gap-2 items-center flex-1">
            {/* Answer choices */}
            {(question.answerType === 1 ? ['A', 'B', 'C', 'D'] : ['A', 'B', 'C', 'D', 'E']).map((choice: string) => {
              const isSelected = answer === choice;
              const isEliminated = eliminated?.has(choice);
              
              return (
                <button
                  key={choice}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCurrentQuestion) {
                      onAnswerSelect(choice as 'A' | 'B' | 'C' | 'D' | 'E');
                    }
                  }}
                  disabled={!isCurrentQuestion}
                  className={`
                    relative w-7 h-7 rounded-full text-sm font-semibold
                    flex items-center justify-center cursor-pointer
                    transition-all duration-200
                    ${isSelected 
                      ? 'bg-orange-500 text-white border-2 border-orange-600 shadow-md scale-110' 
                      : isCurrentQuestion
                        ? 'bg-white text-gray-700 border-2 border-gray-300 hover:border-orange-400 hover:shadow-sm'
                        : 'bg-gray-50 text-gray-400 border-2 border-gray-200'
                    }
                    ${!isCurrentQuestion && 'opacity-60 cursor-not-allowed'}
                  `}
                  title={isCurrentQuestion ? choice : ''}
                >
                  {choice}
                </button>
              );
            })}
          </div>
          
          {/* Clear button - positioned on right border */}
          {isCurrentQuestion && answer && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAnswerSelect('' as any); // Clear the answer
              }}
              className="absolute -right-3 top-1/2 -translate-y-1/2 bg-white rounded-full p-1 shadow-sm text-red-500 hover:text-red-600 transition-colors"
              title="Seçimi temizle"
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path d="M12.5 2.5a1 1 0 011.41 0l4 4a1 1 0 010 1.41l-8 8a1 1 0 01-.7.29H5a1 1 0 01-1-1v-4.2a1 1 0 01.29-.71l8-8a1 1 0 01.71-.29zm0 2.83L6 11.83V14h2.17l6.5-6.5L12.5 5.33z"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  };
  
  return (
    <div className={`absolute top-20 right-4 rounded-xl shadow-xl w-[300px] max-h-[80vh] z-[1000] flex flex-col overflow-hidden ${
      glassEffects !== false 
        ? getGlassClasses(devicePreferences?.preferences?.glassColor || 'crystal', isDark)
        : 'bg-white border border-gray-100'
    }`}>
      {/* Fixed Header */}
      <div className="p-4 pb-0 flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className={`m-0 text-lg font-bold tracking-tight ${
            isDark ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Optik Form
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="#6B7280" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        
        {/* Group Selector - iOS compatible styling */}
        {qbGroups && qbGroups.length > 0 && (
          <div className="mb-3">
            <select
              value={selectedGroupIndex}
              onChange={(e) => {
                setIsUserSelection(true);
                setSelectedGroupIndex(parseInt(e.target.value));
              }}
              className={`w-full px-2.5 py-1.5 rounded-lg border transition-colors text-sm font-medium appearance-none bg-no-repeat bg-right ${
                isDark
                  ? 'bg-gray-800/50 border-gray-600 text-gray-200'
                  : 'bg-white border-gray-300 text-gray-700'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23${isDark ? '9CA3AF' : '6B7280'}' d='M10.293 3.293L6 7.586 1.707 3.293A1 1 0 00.293 4.707l5 5a1 1 0 001.414 0l5-5a1 1 0 10-1.414-1.414z'/%3E%3C/svg%3E")`,
                backgroundPosition: 'right 0.7rem center',
                backgroundSize: '1.2em 1.2em',
                paddingRight: '2.5rem'
              }}
            >
              {qbGroups.map((group, index) => (
                <option key={group.groupId} value={index}>
                  {group.groupName || `Grup ${index + 1}`}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      
      {/* Scrollable Content Area - Only questions scroll */}
      <div className="flex-1 overflow-y-auto px-4 min-h-0">
        {/* Questions List */}
        <div className="py-1">
          {(() => {
            if (!qbGroups || qbGroups.length === 0) {
              // No groups, show all questions
              return questions.map((question, index) => 
                renderQuestionItem(question, index, index + 1)
              );
            } else {
              // Show selected group's questions
              const selectedGroup = qbGroups[selectedGroupIndex];
              if (!selectedGroup) return null;
              
              const groupQuestions: Array<{question: QBTestQuestionEto, globalIndex: number, localNumber: number}> = [];
              
              let localCounter = 1;
              selectedGroup.courses?.forEach(course => {
                course.questions?.forEach(courseQuestion => {
                  if (!courseQuestion.isCommonText) {
                    const globalIndex = questions.findIndex(q => q.questionId === courseQuestion.questionId);
                    if (globalIndex >= 0) {
                      groupQuestions.push({
                        question: questions[globalIndex],
                        globalIndex,
                        localNumber: localCounter++
                      });
                    }
                  }
                });
              });
              
              return groupQuestions.map(({ question, globalIndex, localNumber }) => 
                renderQuestionItem(question, globalIndex, localNumber)
              );
            }
          })()}
        </div>
      </div>
      
      {/* Fixed Progress Section at Bottom - Integrated design */}
      <div className="flex-shrink-0 px-4 pb-4 pt-2">
        {/* Subtle separator */}
        <div className={`h-px w-full mb-3 ${isDark ? 'bg-gray-700/30' : 'bg-gray-200/30'}`} />
        
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>İlerleme</p>
            <p className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              {(() => {
                if (!qbGroups || qbGroups.length === 0) {
                  // Count only real answers (exclude '0' which means unanswered)
                  return `${Object.keys(opticalAnswers).filter(key => opticalAnswers[key] && opticalAnswers[key] !== '0').length} / ${questions.length}`;
                } else {
                  const selectedGroup = qbGroups[selectedGroupIndex];
                  let totalCount = 0;
                  let answeredCount = 0;
                  
                  selectedGroup?.courses?.forEach(course => {
                    course.questions?.forEach(question => {
                      if (!question.isCommonText && question.questionId) {
                        totalCount++;
                        const answer = opticalAnswers[question.questionId];
                        // Count only real answers (exclude '0')
                        if (answer && answer !== '0') {
                          answeredCount++;
                        }
                      }
                    });
                  });
                  
                  return `${answeredCount} / ${totalCount}`;
                }
              })()}
            </p>
          </div>
            
            {/* Progress Circle */}
            <div className="relative w-10 h-10">
              <svg viewBox="0 0 48 48" className="w-10 h-10 -rotate-90">
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  fill="none"
                  stroke={isDark ? '#374151' : '#E5E7EB'}
                  strokeWidth="4"
                />
                <circle
                  cx="24"
                  cy="24"
                  r="20"
                  fill="none"
                  stroke="#FB923C"
                  strokeWidth="4"
                  strokeDasharray={`${(() => {
                    if (!qbGroups || qbGroups.length === 0) {
                      return questions.length > 0
                        ? (Object.keys(opticalAnswers).filter(key => opticalAnswers[key] && opticalAnswers[key] !== '0').length / questions.length) * 125.6
                        : 0;
                    } else {
                      const selectedGroup = qbGroups[selectedGroupIndex];
                      let totalCount = 0;
                      let answeredCount = 0;

                      selectedGroup?.courses?.forEach(course => {
                        course.questions?.forEach(question => {
                          if (!question.isCommonText && question.questionId) {
                            totalCount++;
                            const answer = opticalAnswers[question.questionId];
                            if (answer && answer !== '0') {
                              answeredCount++;
                            }
                          }
                        });
                      });

                      return totalCount > 0 ? (answeredCount / totalCount) * 125.6 : 0;
                    }
                  })()} 125.6`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-sm font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {(() => {
                    if (!qbGroups || qbGroups.length === 0) {
                      return questions.length > 0
                        ? Math.round((Object.keys(opticalAnswers).filter(key => opticalAnswers[key] && opticalAnswers[key] !== '0').length / questions.length) * 100)
                        : 0;
                    } else {
                      const selectedGroup = qbGroups[selectedGroupIndex];
                      let totalCount = 0;
                      let answeredCount = 0;

                      selectedGroup?.courses?.forEach(course => {
                        course.questions?.forEach(question => {
                          if (!question.isCommonText && question.questionId) {
                            totalCount++;
                            const answer = opticalAnswers[question.questionId];
                            if (answer && answer !== '0') {
                              answeredCount++;
                            }
                          }
                        });
                      });

                      return totalCount > 0 ? Math.round((answeredCount / totalCount) * 100) : 0;
                    }
                  })()}%
                </span>
              </div>
            </div>
        </div>
        
        {/* Complete Test Button */}
        {showCompleteButton && onTestComplete && (
          <button
            onClick={() => setShowConfirmModal(true)}
            className={`w-full py-2.5 rounded-lg font-medium transition-all duration-200 ${
              isDark
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            Sınavı Bitir
          </button>
        )}
      </div>
      
      {/* Test Complete Confirmation Modal */}
      <TestCompleteConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={() => {
          setShowConfirmModal(false);
          onTestComplete?.();
        }}
        isMobile={false}
      />
    </div>
  );
};